/**
 * Ending a deaf-to-deaf group call for everyone (web room, host only).
 *
 * In a group call a plain hang-up only leaves: the call goes on for the
 * others (call_end). The host may instead end it for everyone. The host is
 * the person who placed the call (the server's calls.client_id, the stored
 * call's clientId) while they stay; when the host leaves, it passes to the
 * person seated longest, and it does not come back (Robert, 2026-10-05). The
 * server says who with hostId, kept on the stored call:
 *
 *   p2p_end_for_all { callId, roomName }
 *
 * answered with call_ended { callId, roomName, endedBy, endedByName,
 * endedByRole: 'client', reason: 'ended_for_all', surface } to every member,
 * ringing invitee and the host's own devices, or with error { code,
 * operation: 'p2p_end_for_all', callId }: NOT_HOST, NOT_GROUP_CALL,
 * NOT_FOUND, FORBIDDEN, INTERNAL_ERROR. A repeat is acknowledged with
 * call_ended { callId, participantId, reason: 'already_ended' }.
 *
 * Whether the call is a group is the server's word too: groupCall on
 * p2p_ringing / p2p_accepted / p2p_join_room, and any p2p_participant_joined
 * (a direct call becomes a group when a third person is invited). Jitsi
 * moderator status is never used: every member of a platform room holds a
 * moderator token.
 *
 * No storage-layer imports here: the queue middleware's vm harness
 * (groupCallRoom.test.js) loads these helpers as they are.
 */
/** sessionStorage, web only: the notice Home shows after the host ended the call. */
export const CALL_END_NOTICE_KEY = 'vrs_call_end_notice';
/** Home shows the notice only if it arrives this soon after the call ended. */
export const CALL_END_NOTICE_MAX_AGE_MS = 60 * 1000;
/** How long the host waits for the server to confirm the end. */
export const END_FOR_ALL_TIMEOUT_MS = 8000;
export const LEAVE_CALL_LABEL = 'Leave call';
export const END_FOR_ALL_LABEL = 'End call for everyone';
/** The host's hang-up opener and its menu: it no longer only leaves. */
export const HANGUP_MENU_LABEL = 'Leave or end call';
/** Said (to screen readers too) as soon as the host presses End call for everyone. */
export const ENDING_FOR_ALL_STATUS = 'Ending the call for everyone…';
/**
 * The room this page shows: the last part of its path (web rooms live at
 * /<room>), or undefined off a room page.
 *
 * @param {string} pathname - The page path; this page's by default.
 * @returns {string|undefined}
 */
export function pageRoomName(pathname) {
    const path = pathname ?? globalThis.location?.pathname;
    const last = typeof path === 'string' ? path.split('/').filter(Boolean).pop() : undefined;
    if (!last || last.endsWith('.html')) {
        return undefined;
    }
    try {
        return decodeURIComponent(last);
    }
    catch {
        return last;
    }
}
/**
 * Whether this person hosts the live group call stored for this room: the
 * server named them host (hostId), or, before it said, they placed it
 * (clientId, the server's calls.client_id); it is a deaf-to-deaf platform
 * room, the server said it is a group, and it is the room this page is in.
 * The stored call is shared by every tab (localStorage) and can outlive its
 * call, so a call stored for another room never counts.
 *
 * @param {IStoredPlatformCall|null} call - The stored active call (vrs_active_call).
 * @param {string|undefined} userId - The signed-in account.
 * @param {string|null} role - The signed-in role.
 * @param {string|undefined} currentRoom - The room this page is in.
 * @returns {boolean}
 */
export function isGroupCallHost(call, userId, role, currentRoom) {
    const hostId = typeof call?.hostId === 'string' && call.hostId ? call.hostId : call?.clientId;
    return role === 'client'
        && typeof userId === 'string' && userId.length > 0
        && Boolean(call) && hostId === userId
        && typeof call?.callId === 'string' && call.callId.length > 0
        && typeof call?.roomName === 'string' && /^(p2p|group)-/.test(call.roomName)
        && typeof currentRoom === 'string' && currentRoom.toLowerCase() === call.roomName.toLowerCase()
        && call?.groupCall === true;
}
/**
 * What is said when the host ended the call for everyone: "Dana ended the
 * call for everyone." to the others, "You ended the call for everyone." to
 * the host, as confirmation. Nothing for any other end of a call.
 *
 * @param {Object} data - The call_ended payload.
 * @param {string|undefined} localUserId - The signed-in account.
 * @returns {string|undefined}
 */
export function endedForAllNotice(data, localUserId) {
    if (data?.reason !== 'ended_for_all') {
        return undefined;
    }
    const endedBy = typeof data.endedBy === 'string' ? data.endedBy : '';
    if (endedBy && endedBy === localUserId) {
        return 'You ended the call for everyone.';
    }
    const name = typeof data.endedByName === 'string' && data.endedByName.trim() ? data.endedByName.trim() : 'The host';
    return `${name} ended the call for everyone.`;
}
/**
 * This tab's sessionStorage, where there is one.
 *
 * @returns {Storage|undefined}
 */
function sessionStore() {
    try {
        // Web only: native has no tab, and its Home is not this page.
        return globalThis.sessionStorage;
    }
    catch {
        return undefined;
    }
}
/**
 * Keep the notice for Home, which this tab is about to open: the room is
 * torn down before anyone could read a notice shown in it. Tab-scoped and
 * read once; no token or account id, only the words shown.
 *
 * @param {string|undefined} callId - The call that ended.
 * @param {string} message - The notice.
 * @param {number} now - The current time.
 * @returns {void}
 */
export function rememberCallEndNotice(callId, message, now = Date.now()) {
    try {
        sessionStore()?.setItem(CALL_END_NOTICE_KEY, JSON.stringify({
            at: now,
            callId: callId || null,
            message,
            version: 1
        }));
    }
    catch {
        // Storage blocked: the room's own notice is all they get.
    }
}
/**
 * Ask the server to end the call for everyone and wait for its answer: the
 * call_ended for this call (the queue middleware then takes this page home),
 * or its refusal. Not sent at all while offline, so a request cannot linger
 * and end the call after the host was told it had not.
 *
 * @param {IEndForAllService} service - The queue service.
 * @param {Object} call - The call to end.
 * @param {number} timeoutMs - How long to wait.
 * @returns {Promise<EndForAllOutcome>}
 */
export function requestEndForAll(service, call, timeoutMs = END_FOR_ALL_TIMEOUT_MS) {
    let sent = false;
    try {
        sent = service.endGroupCallForAll(call.callId, call.roomName) === true;
    }
    catch {
        sent = false;
    }
    if (!sent) {
        return Promise.resolve({
            code: 'NOT_CONNECTED',
            ok: false
        });
    }
    // The answer comes over the socket, never before this listens for it.
    return new Promise(resolve => {
        let settled = false;
        const finish = (outcome) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            service.off('callEnded', onEnded);
            service.off('error', onError);
            resolve(outcome);
        };
        const onEnded = (data) => {
            if (data?.callId === call.callId) {
                finish({ ok: true });
            }
        };
        const onError = (data) => {
            if (data?.operation === 'p2p_end_for_all' && (!data.callId || data.callId === call.callId)) {
                finish({
                    code: String(data.code || 'INTERNAL_ERROR'),
                    ok: false
                });
            }
        };
        const timer = setTimeout(() => finish({
            code: 'TIMEOUT',
            ok: false
        }), timeoutMs);
        service.on('callEnded', onEnded);
        service.on('error', onError);
    });
}
/**
 * After a refusal: whether the host should simply leave (the call is over,
 * or it was never a group, where hanging up already ends it for both).
 *
 * @param {string} code - The refusal code.
 * @returns {boolean}
 */
export function leaveAfterRefusal(code) {
    return code === 'NOT_GROUP_CALL' || code === 'NOT_FOUND';
}
/**
 * Why the call did not end for everyone, in the host's terms.
 *
 * @param {string} code - The refusal code.
 * @returns {string}
 */
export function endForAllFailureMessage(code) {
    switch (code) {
        case 'NOT_HOST':
        case 'FORBIDDEN':
            return 'Only the host can end the call for everyone.';
        case 'NOT_CONNECTED':
            return 'No connection. Try again.';
        case 'NOT_THIS_ROOM':
            return 'Couldn’t end the call.';
        case 'TIMEOUT':
            // No answer is not a refusal: the server may have ended it already.
            return 'Couldn’t end the call. Try again.';
        default:
            return 'Couldn’t end the call. Try again.';
    }
}
/**
 * The host pressed "End call for everyone". On success the call_ended the
 * server sends takes this page home (queue middleware); nothing else to do.
 * When the call is already gone, or was never a group (hanging up ends a
 * direct call for both), the host simply leaves. Any other refusal is said,
 * and the host stays in the call to try again or leave.
 *
 * @param {Object} deps - The call, the queue service, and how to leave and speak.
 * @returns {Promise<EndForAllOutcome>}
 */
export async function endCallForEveryone({ call, leave, notify, service, timeoutMs }) {
    if (!call?.callId) {
        leave();
        return {
            code: 'NOT_FOUND',
            ok: false
        };
    }
    const outcome = await requestEndForAll(service, call, timeoutMs);
    if (!outcome.ok) {
        if (leaveAfterRefusal(outcome.code)) {
            leave();
        }
        else {
            notify(endForAllFailureMessage(outcome.code));
        }
    }
    return outcome;
}
